import { useState, useEffect, useCallback, useMemo } from 'react';
// import { AnalyticsService } from '../services/analytics.service'; // Removed - using BackendAnalyticsService
// We no longer need the heavy MockAnalyticsService on the client – a minimal
// sample stub keeps type-compatibility while SmartDataManager serves data from
// the config’s exampleResponse arrays.
import { IAnalyticsService, AnalyticsResponse } from '../types/analytics.types';
import { BackendAnalyticsService } from '../services/backend-analytics.service';
import { 
  AnalyticsConfig, 
  AnalyticsRequest,
  AnalyticsDataQuery, 
  AnalyticsDataPoint,
  QueryFilter
} from '../types/analytics.types';
import { useApiConfig } from './useApiConfig';

interface UseAnalyticsProps {
  config: AnalyticsConfig;
  analyticsService: IAnalyticsService;
  defaultFilters?: QueryFilter[];
  refreshInterval?: number;
}

interface UseAnalyticsResult {
  data: AnalyticsDataPoint[];
  loading: boolean;
  error: string | null;
  filters: QueryFilter[];
  setFilters: (filters: QueryFilter[]) => void;
  refresh: () => Promise<void>;
  metadata: {
    lastRefreshed: Date | null;
    totalRecords: number;
    cacheHit: boolean;
  };
}

// Simple service interface for dashboard use
interface UseAnalyticsServiceResult {
  service: {
    processAnalytics: (request: AnalyticsDataQuery) => Promise<AnalyticsResponse>;
  };
}

interface UseAnalyticsServiceProps {
  useMockData?: boolean;
  dashboardConfig?: AnalyticsConfig;
  enableLegacyFallback?: boolean; // Option to enable config-driven fallback
  dashboardKey?: string; // The dashboard.key for application routing
}

class SampleAnalyticsService implements IAnalyticsService {
  async processAnalytics(): Promise<AnalyticsResponse> {
    // Should never be reached because SmartDataManager short-circuits when
    // exampleResponse is present, but return an empty dataset for safety.
    return {
      configId: 'sample',
      data: [],
      metadata: {
        totalRecords: 0,
        processingTime: 0,
        cacheHit: true,
        dataSource: 'static-sample',
        lastRefreshed: new Date()
      }
    } as AnalyticsResponse;
  }

  // Minimal stubs to satisfy the interface (never called in sample mode)
  async getConfig(_configId: string): Promise<AnalyticsConfig> { throw new Error('Not implemented'); }
  async getConfigs(): Promise<AnalyticsConfig[]> { return []; }
  async processTabAnalytics(): Promise<AnalyticsResponse> { return this.processAnalytics(); }
  async getFilterOptions(): Promise<any[]> { return []; }
  async expandHierarchyNode(): Promise<any[]> { return []; }
  async collapseHierarchyNode(): Promise<void> { return; }
}

export const useAnalytics = ({
  config,
  analyticsService,
  defaultFilters = [],
  refreshInterval
}: UseAnalyticsProps): UseAnalyticsResult => {
  const [data, setData] = useState<AnalyticsDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<QueryFilter[]>(defaultFilters);
  const [metadata, setMetadata] = useState({
    lastRefreshed: null as Date | null,
    totalRecords: 0,
    cacheHit: false
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const query: AnalyticsDataQuery = {
        configId: config.id,
        metrics: config.metrics.map(m => m.id),
        dimensions: config.dimensions.map(d => d.id),
        groupBy: config.dimensions.map(d => d.id),
        filters: filters.map(f => ({ field: f.field, operator: 'eq' as const, value: f.value })),
        limit: 1000
      };

      const response: AnalyticsResponse = await analyticsService.processAnalytics(query);
      
      setData(response.data);
      setMetadata({
        lastRefreshed: new Date(),
        totalRecords: response.metadata.totalRecords,
        cacheHit: response.metadata.cacheHit
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [config, analyticsService, filters]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh interval
  useEffect(() => {
    if (!refreshInterval || refreshInterval <= 0) return;

    const interval = setInterval(() => {
      fetchData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval, fetchData]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    filters,
    setFilters,
    refresh,
    metadata
  };
};

// Simple service hook for dashboard usage
export const useAnalyticsService = ({ useMockData = true, dashboardConfig, enableLegacyFallback = false, dashboardKey }: UseAnalyticsServiceProps = {}): UseAnalyticsServiceResult => {
  const { baseUrl, appKey, getAuthToken } = useApiConfig({ dashboardConfig, dashboardKey });
  
  const service = useMemo(() => {
    if (useMockData) {
      // Lightweight sample service
      const mockService = new SampleAnalyticsService();
      return {
        processAnalytics: async (request: AnalyticsDataQuery): Promise<AnalyticsResponse> => {
          // Should rarely be hit; return empty array to keep typing happy.
          console.warn('SampleAnalyticsService.processAnalytics fallback – returning empty dataset');
          return mockService.processAnalytics();
        }
      };
    } else {
      // Backend service with dynamic API configuration and authentication
      const backendService = new BackendAnalyticsService(baseUrl, appKey, getAuthToken, enableLegacyFallback);
      
      // Set dashboard configuration for dynamic endpoint building
      if (dashboardConfig) {
        backendService.setDashboardConfig(dashboardConfig);
      }
      
      return {
        processAnalytics: async (request: AnalyticsDataQuery): Promise<AnalyticsResponse> => {
          return backendService.processAnalytics(request);
        }
      };
    }
  }, [useMockData, baseUrl, appKey, getAuthToken, dashboardConfig, enableLegacyFallback, dashboardKey]);

  return { service };
}; 